// tickets / ticket_services realtime → NUDGE + server refetch (sb_jwt Phase 1).
//
// The browser token (sb_jwt, role authenticated, no tier claim) used to read
// tickets and ticket_services in full, because data-context built tickets
// straight from postgres_changes payloads (mapTicketRow(payload.new)) and
// re-read ticket_services with select('*'). That made every money column —
// tickets.total / payment_splits / payment_detail / discount / invoiced /
// propina + propina_staff_id, ticket_services.price / material_cost — readable
// by every staffer's token for the clinic's WHOLE history over /rest/v1.
//
// Since 20261003_authenticated_least_privilege_phase1.sql the token reads only
//   tickets(id, clinic_id, status, deleted, patient_id, closed_at)
//   ticket_services(id, clinic_id, ticket_id)
// and Realtime trims payloads to those columns. So a payload can no longer
// BE the ticket: an event only says "ticket X changed", and the ticket is
// re-read through GET /api/tickets/[id] (requireRole 'staff', clinic-scoped,
// the same mapTicketRow pipeline /api/data uses). Bursts (a close writes the
// ticket and replaces its lines → several events) coalesce into ONE refetch
// per ticket.
//
// Do not read any other column from these payloads: it is not there.

import type { Ticket } from './types';

export type TicketEventSource = 'tickets' | 'ticket_services';

/** The parts of a RealtimePostgresChangesPayload this module reads. */
export interface TicketChange {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  new?: Record<string, unknown> | null;
  old?: Record<string, unknown> | null;
}

export interface TicketListView {
  /** The ticket is in the client list (active + today's closed). */
  isKnown(id: string): boolean;
  /** Optimistically deleted by this tab. */
  isDeleted(id: string): boolean;
  /** Optimistically created by this tab, not yet confirmed. */
  isPending(id: string): boolean;
  /** A refetch for this ticket is queued or in flight. */
  isScheduled(id: string): boolean;
}

export type TicketChangeAction =
  | { kind: 'ignore' }
  | { kind: 'remove'; id: string }
  | { kind: 'refetch'; id: string; allowInsert: boolean };

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** Decide what one event means for the client ticket list. Only the granted
 *  columns are read: tickets.id / tickets.deleted, ticket_services.ticket_id. */
export function ticketChangeAction(source: TicketEventSource, change: TicketChange, view: TicketListView): TicketChangeAction {
  const n = change.new ?? {};
  const o = change.old ?? {};
  if (source === 'tickets') {
    if (change.eventType === 'DELETE') {
      const id = str(o.id);
      return id ? { kind: 'remove', id } : { kind: 'ignore' };
    }
    const id = str(n.id);
    if (!id) return { kind: 'ignore' };
    if (change.eventType === 'INSERT') {
      if (n.deleted === true || view.isDeleted(id) || view.isPending(id)) return { kind: 'ignore' };
      return { kind: 'refetch', id, allowInsert: true };
    }
    if (change.eventType === 'UPDATE') {
      if (n.deleted === true) return { kind: 'remove', id };
      if (view.isDeleted(id)) return { kind: 'ignore' };
      // A historical ticket edited elsewhere is not in this list (it only
      // holds active + today's closed) — nothing to refresh.
      return view.isKnown(id) || view.isScheduled(id) ? { kind: 'refetch', id, allowInsert: false } : { kind: 'ignore' };
    }
    return { kind: 'ignore' };
  }
  // ticket_services. A DELETE does NOT carry ticket_id: the table is REPLICA
  // IDENTITY FULL, but Supabase Realtime cuts `old` down to the primary key on
  // every RLS-enabled table ("we can't secure deletes"), so a line DELETE is
  // ignored here. Line removals still reach other tabs because every writer
  // that deletes lines also UPDATEs tickets.total afterwards
  // (replace_ticket_services step 4, and updateTicketData's legacy path via
  // CE-7) — that tickets UPDATE is the nudge. A future path that deletes lines
  // WITHOUT touching the tickets row would leave other tabs stale.
  // (o.ticket_id is read only in case a future Realtime ever sends it.)
  const ticketId = str(n.ticket_id) ?? str(o.ticket_id);
  if (!ticketId || view.isDeleted(ticketId)) return { kind: 'ignore' };
  return view.isKnown(ticketId) || view.isScheduled(ticketId)
    ? { kind: 'refetch', id: ticketId, allowInsert: false }
    : { kind: 'ignore' };
}

/** null = the ticket is gone (404: deleted / other clinic); 'error' = keep
 *  what the list has (network, 5xx, 401 — the heartbeat owns logouts). */
export type FetchedTicket = Ticket | null | 'error';

export async function fetchTicketForList(id: string, fetchImpl: typeof fetch = fetch): Promise<FetchedTicket> {
  try {
    const res = await fetchImpl(`/api/tickets/${encodeURIComponent(id)}`, { credentials: 'include', cache: 'no-store' });
    if (res.status === 404) return null;
    if (!res.ok) return 'error';
    const body = (await res.json()) as { ticket?: Ticket };
    return body?.ticket ?? 'error';
  } catch {
    return 'error';
  }
}

export interface TicketRefetcher {
  schedule(id: string, allowInsert: boolean): void;
  /** Queued OR in flight — an event for this ticket must not be dropped as
   *  "unknown" while its first fetch (e.g. right after its INSERT) runs. */
  isScheduled(id: string): boolean;
  dispose(): void;
}

/** Per-ticket debounced refetch, SERIALIZED per ticket.
 *
 *  · A burst of events for one ticket → one fetch; `allowInsert` is sticky
 *    within a burst (an INSERT followed by line inserts still adds the ticket).
 *  · At most ONE fetch per ticket is in flight. An event that arrives while it
 *    is in flight does not start a second, overlapping fetch (whose response
 *    could land first and then be overwritten by the older one, leaving the
 *    list stale with no later event to correct it); it marks the ticket dirty,
 *    and exactly one follow-up fetch is debounced once the current one settles
 *    — whatever it returned, error included. allowInsert stays sticky across
 *    the follow-up, so an INSERT whose first fetch failed is still added.
 *  · Every response also carries a per-ticket generation; anything older than
 *    the last one applied is dropped (belt and braces over the serialization).
 *  · Results that arrive after dispose() are dropped. */
export function createTicketRefetcher(opts: {
  fetchTicket: (id: string) => Promise<FetchedTicket>;
  apply: (id: string, ticket: Ticket | null, allowInsert: boolean) => void;
  debounceMs?: number;
}): TicketRefetcher {
  const debounceMs = opts.debounceMs ?? 300;
  const queued = new Map<string, { timer: ReturnType<typeof setTimeout>; allowInsert: boolean }>();
  /** id → its fetch in flight: did events arrive meanwhile, and did any allow insert */
  const inFlight = new Map<string, { dirty: boolean; allowInsert: boolean }>();
  const issued = new Map<string, number>();
  const applied = new Map<string, number>();
  let disposed = false;

  function run(id: string, allowInsert: boolean) {
    const gen = (issued.get(id) ?? 0) + 1;
    issued.set(id, gen);
    inFlight.set(id, { dirty: false, allowInsert: false });
    void opts.fetchTicket(id).then(
      (t) => settle(id, gen, t, allowInsert),
      () => settle(id, gen, 'error', allowInsert),
    );
  }

  function settle(id: string, gen: number, t: FetchedTicket, allowInsert: boolean) {
    if (disposed) return;
    const flight = inFlight.get(id);
    inFlight.delete(id);
    if (t !== 'error' && gen > (applied.get(id) ?? 0)) {
      applied.set(id, gen);
      opts.apply(id, t, allowInsert);
    }
    if (flight?.dirty) {
      // An insert that failed to fetch keeps its right to be inserted.
      schedule(id, flight.allowInsert || (t === 'error' && allowInsert));
    }
  }

  function schedule(id: string, allowInsert: boolean) {
    if (disposed) return;
    const flight = inFlight.get(id);
    if (flight) {
      flight.dirty = true;
      flight.allowInsert = flight.allowInsert || allowInsert;
      return;
    }
    const prev = queued.get(id);
    if (prev) clearTimeout(prev.timer);
    const sticky = allowInsert || !!prev?.allowInsert;
    const timer = setTimeout(() => {
      queued.delete(id);
      run(id, sticky);
    }, debounceMs);
    queued.set(id, { timer, allowInsert: sticky });
  }

  return {
    schedule,
    isScheduled: (id) => queued.has(id) || inFlight.has(id),
    dispose() {
      disposed = true;
      queued.forEach((q) => clearTimeout(q.timer));
      queued.clear();
      inFlight.clear();
    },
  };
}

/** Fold a refetched ticket into the client list. Mirrors the old payload
 *  handler's guards: optimistic deletes win, a pending local create is not
 *  duplicated, and in-flight optimistic edits stay on top of server data. */
export function applyRefetchedTicket(
  prev: Ticket[],
  id: string,
  ticket: Ticket | null,
  allowInsert: boolean,
  guards: { isDeleted(id: string): boolean; isPending(id: string): boolean; pendingUpdate(id: string): Partial<Ticket> | undefined },
): Ticket[] {
  if (ticket === null) return prev.some((t) => t.id === id) ? prev.filter((t) => t.id !== id) : prev;
  if (guards.isDeleted(id)) return prev;
  const i = prev.findIndex((t) => t.id === id);
  if (i === -1) {
    if (!allowInsert || guards.isPending(id)) return prev;
    return [ticket, ...prev];
  }
  const updates = guards.pendingUpdate(id);
  const merged = updates ? ({ ...ticket, ...updates } as Ticket) : ticket;
  return prev.map((t) => (t.id === id ? merged : t));
}
